var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createStyles, makeStyles } from '@hawkins/styles';
var validationBorderAnchorSelector = '&:not($rootBorderless) $validationBorderAnchor';
var validationBorderAnchorZIndex = 1;
var focusRingAnchorStyle = {
    bottom: 0,
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: validationBorderAnchorZIndex + 1,
};
export function inputStyles(theme) {
    var _a, _b, _c, _d, _e;
    var hawkins = theme.hawkins, hawkinsUtils = theme.hawkinsUtils, hawkinsVariables = theme.hawkinsVariables;
    var isDarkTheme = hawkins.theme === 'dark';
    var focusRingedElementStyle = hawkinsUtils.getFocusRingedElementStyle({ position: 'absolute' }, hawkinsUtils.getFocusRingPosition(1));
    var borderWidth = hawkinsVariables.borderWidthValues.borderWidth0;
    var inputHeight = hawkinsVariables.sizings.size4;
    var inputHeightCompact = hawkinsVariables.sizings.size3;
    var paddingX = hawkinsVariables.spacings.space2;
    return createStyles({
        /** Class name of each element containing a start or end adornment. */
        adornmentContainer: {
            alignItems: 'center',
            display: 'flex',
        },
        /** Class name of each adornment container when the component is a multi-line input. */
        adornmentContainerMultiline: {
            paddingTop: hawkinsVariables.spacingValues.space2 - borderWidth,
        },
        /** Class name of each adornment container when the component is a compact multi-line input. */
        adornmentContainerMultilineCompact: {
            paddingTop: hawkinsVariables.spacingValues.space1 - borderWidth,
        },
        /** Class name of the disabled icon. */
        disabledIcon: {
            cursor: 'not-allowed',
        },
        /** Class name of the element containing the start adornment. */
        endAdornmentContainer: {
            // We set horizontal padding on each child element (`startAdornmentContainer`, `input`, and `endAdornmentContainer`)
            // instead of setting padding + flexbox gap on the container element, because doing that results in the wrong styling
            // in a multi-line input with a scrollbar and no adornments.
            paddingRight: paddingX,
        },
        /** Class name of the focus ring anchor element. */
        focusRingAnchor: focusRingAnchorStyle,
        /** Class name of the native `<input>` or `<textarea>`. */
        input: (_a = {
                appearance: 'none',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'inherit',
                flexGrow: 1,
                fontFamily: 'inherit',
                fontSize: 'inherit',
                height: '100%',
                lineHeight: 'inherit',
                minWidth: 0,
                outline: 'none',
                padding: "".concat((hawkinsVariables.spacingValues.space1 * 5) / 4 - borderWidth, "px ").concat(paddingX)
            },
            _a[hawkinsUtils.getFocusRingedElementSelector('$root:not($rootPlain) &')] = {
                '& ~ $focusRingAnchor': focusRingedElementStyle,
            },
            // Remove the default `<input type="search">` styling in WebKit browsers (see https://stackoverflow.com/a/9422689).
            _a['&::-webkit-search-decoration, &::-webkit-search-cancel-button, &::-webkit-search-results-button, &::-webkit-search-results-decoration'] = { appearance: 'none' },
            _a['&::placeholder'] = {
                color: hawkins.textColor.textColorLight,
                opacity: 1,
            },
            _a),
        /** Class name of the native `<input>` or `<textarea>` when the component is compact. */
        inputCompact: {
            padding: "".concat(hawkinsVariables.spacingValues.space1 - borderWidth, "px ").concat(paddingX),
        },
        /** Class name of the native `<input>` or `<textarea>` when the component is disabled. */
        inputDisabled: {
            cursor: 'not-allowed',
            '&::placeholder': { color: hawkins.textColor.textColorXlight },
        },
        /** Class name of the native <textarea> when the component supports multi-line input. */
        inputMultiline: {
            resize: 'none',
        },
        /** Class name of the root element. */
        root: {
            alignItems: 'center',
            backgroundClip: 'padding-box',
            backgroundColor: isDarkTheme ? hawkins.highlight.backgroundColorRegular : hawkins.ui.backgroundColor0,
            borderColor: hawkins.ui.borderColorXlight,
            borderRadius: hawkinsVariables.borderRadius.borderRadius0,
            borderStyle: 'solid',
            borderWidth: borderWidth,
            color: hawkins.textColor.textColorRegular,
            display: 'flex',
            fontFamily: hawkinsVariables.fontFamily.fontFamilySans,
            fontSize: hawkins.body.fontSize0,
            height: inputHeight,
            lineHeight: hawkins.body.lineHeightRegular,
            minHeight: inputHeight,
            position: 'relative',
        },
        /** Class name of the root element when the component is active. */
        rootActive: {
            '& $focusRingAnchor': focusRingedElementStyle,
        },
        /** Class name of the root element when the component is borderless. */
        rootBorderless: {
            // Make sure the background color (if any) takes up the entire component area since the border is transparent.
            backgroundClip: 'border-box',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
        },
        /** Class name of the root element when the component is compact. */
        rootCompact: {
            fontSize: hawkins.label.fontSize0,
            height: inputHeightCompact,
            minHeight: inputHeightCompact,
        },
        /** Class name of the root element when the component is disabled. */
        rootDisabled: {
            backgroundColor: isDarkTheme ? hawkins.highlight.backgroundColorRegular : hawkins.ui.backgroundColor2,
            color: hawkins.textColor.textColorXlight,
        },
        /** Class name of the root element when the component is a multi-line input. */
        rootMultiline: {
            alignItems: 'flex-start',
            height: 'auto',
        },
        /** Class name of the root element when the component has plain styling. */
        rootPlain: {},
        /** Class name of the root element when a validation status is set. */
        rootValidated: (_b = {},
            _b[validationBorderAnchorSelector] = focusRingedElementStyle,
            _b),
        /** Class name of the root element when the validation status is `"error"`. */
        rootValidatedError: (_c = {
                backgroundColor: hawkins.validation.backgroundColorError
            },
            _c["".concat(validationBorderAnchorSelector, "::after")] = { borderColor: hawkins.information.borderColorError },
            _c),
        /** Class name of the root element when the validation status is `"success"`. */
        rootValidatedSuccess: (_d = {
                backgroundColor: hawkinsVariables.colors.greenT10
            },
            _d["".concat(validationBorderAnchorSelector, "::after")] = { borderColor: hawkins.information.borderColorSuccess },
            _d),
        /** Class name of the root element when the validation status is `"warning"`. */
        rootValidatedWarning: (_e = {
                backgroundColor: hawkinsVariables.colors.yellowT10
            },
            _e["".concat(validationBorderAnchorSelector, "::after")] = { borderColor: hawkins.information.borderColorWarning },
            _e),
        /** Class name of the element containing the start adornment. */
        startAdornmentContainer: {
            // We set horizontal padding on each child element (`startAdornmentContainer`, `input`, and `endAdornmentContainer`)
            // instead of setting padding + flexbox gap on the container element, because doing that results in the wrong styling
            // in a multi-line input with a scrollbar and no adornments.
            paddingLeft: paddingX,
        },
        /** Class name of the validation border anchor element. */
        validationBorderAnchor: __assign(__assign({}, focusRingAnchorStyle), { zIndex: validationBorderAnchorZIndex }),
    });
}
export var useInputStyles = makeStyles(inputStyles, { name: 'Hawkins-Input' });
